import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Loader2, Lock, Mail } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { applyStoredLanguage, VENDOR_LANGUAGE_STORAGE_KEY } from "@/lib/languageStorage";
import { isVendorAuthenticated, vendorLogin } from "@/lib/vendorAuth";
import {
  fetchMyVendorClaims,
  setStoredVendorVenueId,
  type VendorClaim,
} from "@/lib/vendorPortal";

export const Route = createFileRoute("/vendor/login")({
  head: () => ({
    meta: [
      { title: "Venue Portal · Pulse" },
      { name: "description", content: "Login for Pulse venue partners." },
    ],
  }),
  component: VendorLoginPage,
});

function routeForClaims(claims: VendorClaim[]): {
  to: "/vendor/dashboard" | "/vendor/pending" | "/vendor/register-venue";
  approvedVenueId?: string;
} {
  const approved = claims.find((c) => c.status === "approved");
  if (approved) return { to: "/vendor/dashboard", approvedVenueId: approved.venueId };
  if (claims.some((c) => c.status === "pending")) return { to: "/vendor/pending" };
  return { to: "/vendor/register-venue" };
}

function VendorLoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation("vendor");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const goToClaimDestination = async () => {
    const claims = await fetchMyVendorClaims();
    const dest = routeForClaims(claims);
    if (dest.approvedVenueId) setStoredVendorVenueId(dest.approvedVenueId);
    void navigate({ to: dest.to, replace: true });
  };

  useEffect(() => {
    applyStoredLanguage(VENDOR_LANGUAGE_STORAGE_KEY);
  }, []);

  useEffect(() => {
    if (!isVendorAuthenticated()) return;
    let cancelled = false;
    void (async () => {
      const claims = await fetchMyVendorClaims();
      const dest = routeForClaims(claims);
      if (cancelled || dest.to === "/vendor/register-venue") return;
      if (dest.approvedVenueId) setStoredVendorVenueId(dest.approvedVenueId);
      void navigate({ to: dest.to, replace: true });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setSubmitting(true);
    setError(null);
    try {
      await vendorLogin(email.trim(), password);
      await goToClaimDestination();
    } catch (err) {
      const message = err instanceof Error ? err.message : t("login.signInFailed");
      setError(/invalid credentials/i.test(message) ? t("login.invalidCredentials") : message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen px-5 pb-12 pt-14">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-56 bg-gradient-glow" />

      <div className="relative mx-auto max-w-md">
        <div className="flex items-start gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-pulse/15 text-pulse">
            <Building2 className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
              Venue Portal
            </p>
            <h1 className="text-2xl font-bold">{t("login.title")}</h1>
          </div>
          <LanguageSwitcher className="mt-0.5" storageKey={VENDOR_LANGUAGE_STORAGE_KEY} />
        </div>

        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t("login.subtitle")}</p>

        <form
          onSubmit={handleSubmit}
          className="mt-6 space-y-4 rounded-2xl border border-border bg-surface-elevated p-4"
        >
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-muted-foreground">
              {t("login.email")}
            </span>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("login.emailPlaceholder")}
                required
                className="w-full rounded-xl border border-border bg-surface py-3 pl-10 pr-4 text-sm outline-none transition-colors focus:border-pulse"
              />
            </div>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-muted-foreground">
              {t("login.password")}
            </span>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="w-full rounded-xl border border-border bg-surface py-3 pl-10 pr-4 text-sm outline-none transition-colors focus:border-pulse"
              />
            </div>
          </label>

          {error && (
            <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs font-medium text-red-400">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting || !email.trim() || !password}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-pulse px-6 py-3 text-sm font-bold text-pulse-foreground shadow-pulse transition-transform disabled:opacity-50 active:scale-95"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("login.signIn")}
          </button>

          <div className="flex items-center justify-end pt-1">
            <Link
              to="/vendor/register-venue"
              className="text-xs font-semibold text-pulse hover:underline"
            >
              {t("login.noAccount")} {t("login.registerVenue")}
            </Link>
          </div>
        </form>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          {t("login.newHere")}{" "}
          <a href="/for-venues" className="font-semibold text-pulse hover:underline">
            {t("login.learnMore")}
          </a>
        </p>
      </div>
    </div>
  );
}
